/**
 * Configuration déclarative pour l'entité Contact
 * Source unique de vérité pour tous les champs de Contact
 */

import type { Contact, ContactStatus } from '../../types/contact';
import { defineEntityConfig } from '../../lib/entity-config';
import { CONTACT_STATUSES, CONTACT_STATUS_LABELS } from '../../types/contact';
import { COUNTRIES } from '../../types/school';

export const CONTACT_CONFIG = defineEntityConfig<Contact>({
  entityName: 'contact',
  entityNamePlural: 'contacts',

  fields: {
    last_name: {
      key: 'last_name',
      label: 'Nom',
      type: 'text',
      required: true,
      sortable: true,
      searchable: true,
      showInTable: true,
      showInFilters: true,
      exportable: true,
      importable: true,
      size: 150,
    },

    first_name: {
      key: 'first_name',
      label: 'Prénom',
      type: 'text',
      required: true,
      sortable: true,
      searchable: true,
      showInTable: true,
      showInFilters: true,
      exportable: true,
      importable: true,
      size: 150,
    },

    email: {
      key: 'email',
      label: 'Email',
      type: 'text',
      required: true,
      sortable: true,
      searchable: true,
      showInTable: true,
      showInFilters: true,
      exportable: true,
      importable: true,
      size: 200,
      importValidation: (value) => {
        if (typeof value !== 'string') return "L'email doit être une chaîne de caractères";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Email invalide';
        return true;
      },
    },

    phone: {
      key: 'phone',
      label: 'Téléphone',
      type: 'text',
      sortable: false,
      searchable: true,
      showInTable: true,
      showInFilters: true,
      exportable: true,
      importable: true,
      size: 150,
    },

    status: {
      key: 'status',
      label: 'Statut',
      type: 'enum',
      enumValues: CONTACT_STATUSES as any,
      required: true,
      sortable: true,
      editable: true,
      showInTable: true,
      showInFilters: true,
      exportable: true,
      importable: true,
      size: 180,
      exportTransform: (value: ContactStatus) => CONTACT_STATUS_LABELS[value] || value,
      importTransform: (value: string) => {
        // Accepte le label français ou la valeur technique
        const normalized = value.toLowerCase().trim();
        if (normalized === 'prospect') return 'prospect';
        if (
          normalized === 'pré-inscrit' ||
          normalized === 'pre-inscrit' ||
          normalized === 'pre_registered'
        )
          return 'pre_registered';
        if (
          normalized === 'bed téléchargé' ||
          normalized === 'bed telecharge' ||
          normalized === 'bed_downloaded'
        )
          return 'bed_downloaded';
        if (
          normalized === 'bed incomplet — paiement' ||
          normalized === 'bed incomplet - paiement' ||
          normalized === 'bed_incomplete_payment'
        )
          return 'bed_incomplete_payment';
        // Libellé historique « BED incomplet » (avant 00178) -> infos manquantes.
        if (
          normalized === 'bed incomplet' ||
          normalized === 'bed incomplet — infos' ||
          normalized === 'bed incomplet - infos' ||
          normalized === 'bed_incomplete'
        )
          return 'bed_incomplete';
        if (normalized === 'bed complet' || normalized === 'bed_complete') return 'bed_complete';
        if (normalized === 'annulation conforme' || normalized === 'cancellation_compliant')
          return 'cancellation_compliant';
        if (normalized === 'annulation non conforme' || normalized === 'cancellation_non_compliant')
          return 'cancellation_non_compliant';
        if (normalized === 'report conforme' || normalized === 'postponement_compliant')
          return 'postponement_compliant';
        if (normalized === 'report non conforme' || normalized === 'postponement_non_compliant')
          return 'postponement_non_compliant';
        if (
          normalized === 'terminé' ||
          normalized === 'completed' ||
          normalized === 'inscrit définitif'
        )
          return 'completed';
        return 'prospect'; // Défaut
      },
    },

    origin: {
      key: 'origin',
      label: 'Origine',
      type: 'relation',
      sortable: true,
      showInTable: false, // Masqué par défaut, activable via sélecteur de colonnes
      hiddenInColumnSelector: false, // Disponible dans le sélecteur de colonnes
      showInFilters: true,
      exportable: true,
      importable: false,
      exportTransform: (origin: any) => origin?.label || origin?.name || '',
    },

    products: {
      key: 'products',
      label: 'Produits',
      type: 'relation',
      sortable: false,
      showInTable: true,
      showInFilters: false,
      exportable: true,
      importable: false, // Array de relations, trop complexe
      exportTransform: (products: any) => {
        if (!products || !Array.isArray(products) || products.length === 0) return '';
        return products.map((p: any) => p.name).join('; ');
      },
    },

    assigned_user: {
      key: 'assigned_user',
      label: 'Assigné à',
      type: 'relation',
      sortable: false,
      showInTable: false, // Masqué par défaut, disponible dans le sélecteur
      showInFilters: false,
      exportable: true,
      importable: false, // Relation complexe
      exportTransform: (user: any) => user?.full_name || user?.email || '',
    },

    notes: {
      key: 'notes',
      label: 'Notes',
      type: 'text',
      sortable: false,
      showInTable: false,
      showInFilters: true,
      exportable: true, // Exportable si colonne activée
      importable: true,
      exportTransform: (value: string | null) => value || '',
    },

    // Champs d'adresse
    address: {
      key: 'address',
      label: 'Adresse',
      type: 'text',
      sortable: false,
      showInTable: false,
      showInFilters: true,
      exportable: true,
      importable: true,
    },

    city: {
      key: 'city',
      label: 'Ville',
      type: 'text',
      sortable: true,
      searchable: true,
      showInTable: false,
      showInFilters: true,
      exportable: true,
      importable: true,
    },

    postal_code: {
      key: 'postal_code',
      label: 'Code Postal',
      type: 'text',
      sortable: false,
      showInTable: false,
      showInFilters: true,
      exportable: true,
      importable: true,
    },

    country: {
      key: 'country',
      label: 'Pays',
      type: 'enum',
      enumValues: COUNTRIES.map((c) => c.code) as any,
      required: false,
      sortable: false,
      showInTable: false,
      showInFilters: true,
      exportable: true,
      importable: true,
      size: 120,
      exportTransform: (value: string) => {
        const country = COUNTRIES.find((c) => c.code === value);
        return country?.label || value;
      },
      importTransform: (value: string) => {
        // Accepte code ou label
        const byCode = COUNTRIES.find((c) => c.code === value);
        if (byCode) return byCode.code;
        const byLabel = COUNTRIES.find((c) => c.label.toLowerCase() === value.toLowerCase());
        return byLabel?.code || 'FRA'; // Défaut: France
      },
    },

    // Statut étudiant
    is_not_student: {
      key: 'is_not_student',
      label: 'Plus étudiant',
      type: 'boolean',
      sortable: false,
      showInTable: false,
      showInFilters: true,
      exportable: true,
      importable: true,
      exportTransform: (value: boolean) => (value ? 'Oui' : 'Non'),
      importTransform: (value: string) => {
        const normalized = value.toLowerCase().trim();
        return normalized === 'oui' || normalized === 'true' || normalized === '1';
      },
    },

    socio_professional_category: {
      key: 'socio_professional_category',
      label: 'Catégorie socio-professionnelle',
      type: 'text',
      sortable: false,
      showInTable: false,
      showInFilters: true,
      exportable: true,
      importable: true,
    },

    newsletter_opt_in: {
      key: 'newsletter_opt_in',
      label: 'Newsletter',
      type: 'boolean',
      sortable: false,
      showInTable: false,
      showInFilters: true,
      exportable: true,
      importable: true,
      exportTransform: (value: boolean) => (value ? 'Inscrit' : 'Désinscrit'),
      importTransform: (value: string) => {
        const normalized = value.toLowerCase().trim();
        return normalized === 'inscrit' || normalized === 'true' || normalized === '1';
      },
    },

    // Retour réunion 07/07 (E) : n° BPI du contact (contacts_filter_view via
    // form_submissions, migration 00151). Searchable (n° BPI + n° BED via
    // substitution BED→BPI, cf. postgrestSearch) et colonne « N° BPI »
    // disponible dans le sélecteur (masquée par défaut). Non triable / non
    // filtrable (hors whitelist de tri de la vue). Non recherchable tant que la
    // colonne n'est pas en base : garde runtime dans useContacts
    // (resolveContactSearchFields) — pas de 400 avant migration.
    bpi_code: {
      key: 'bpi_code',
      label: 'N° BPI',
      type: 'text',
      sortable: false,
      searchable: true,
      showInTable: false,
      hiddenInColumnSelector: false,
      showInFilters: false,
      exportable: true,
      importable: false,
      size: 140,
    },

    // Dernier appel (calculé depuis timeline_events)
    last_call_at: {
      key: 'last_call_at',
      label: 'Dernier appel',
      type: 'date',
      sortable: true,
      showInTable: true,
      showInFilters: true,
      exportable: true,
      importable: false,
      exportTransform: (value: string | null) => {
        if (!value) return '-';
        return new Date(value).toLocaleString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
    },

    created_at: {
      key: 'created_at',
      label: 'Créé le',
      type: 'date',
      sortable: true,
      showInTable: false,
      showInFilters: true,
      exportable: true,
      importable: false,
      exportTransform: (value: string | null) => {
        if (!value) return '-';
        return new Date(value).toLocaleString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
    },

    updated_at: {
      key: 'updated_at',
      label: 'Modifié le',
      type: 'date',
      sortable: true,
      showInTable: false,
      showInFilters: true,
      exportable: true,
      importable: false,
      exportTransform: (value: string | null) => {
        if (!value) return '-';
        return new Date(value).toLocaleString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
    },
  },
});
